/**
 * Strix Healthcare — Auth Context
 *
 * Dual-mode: uses Clerk when VITE_CLERK_PUBLISHABLE_KEY is configured,
 * falls back to demo localStorage auth for POC/development.
 */

import React, { createContext, useContext, useState, useEffect } from 'react';
import { useUser as useClerkUser, useAuth as useClerkAuth } from '@clerk/clerk-react';
import { homePathForRole } from '../lib/portal-scope';

const USE_CLERK = !!import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;

type AuthRole = 'patient' | 'provider' | 'admin' | 'super_admin' | 'non_member';

interface AuthUser {
  id: string;
  email: string;
  fullName: string;
  role: AuthRole;
  sessionId: string;
}

interface AuthContextType {
  isLoaded: boolean;
  isSignedIn: boolean;
  user: AuthUser | null;
  signIn: (role?: AuthRole) => void | Promise<void>;
  signOut: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ─── Demo mode (no Clerk key) ──────────────────────────────────────────────

const DEMO_STORAGE_KEY = 'strix_demo_user';
const DEMO_TOKEN_KEY = 'strix_demo_token';

/** Mint a server-accepted JWT for a demo role and store it. Returns true on success. */
async function mintDemoToken(role: string): Promise<boolean> {
  try {
    const r = await fetch('/api/auth/demo-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ role }),
    });
    if (!r.ok) return false;
    const data = (await r.json()) as { token?: string };
    if (data?.token) {
      localStorage.setItem(DEMO_TOKEN_KEY, data.token);
      return true;
    }
    return false;
  } catch {
    return false;
  }
}

const DEMO_USERS: Record<AuthRole, AuthUser> = {
  admin: { id: 'demo-admin', email: 'admin@strixgov.com', fullName: 'Dr. Sarah Chen', role: 'admin', sessionId: 'demo-admin' },
  provider: { id: 'demo-provider', email: 'provider@strixgov.com', fullName: 'Dr. Maria Rodriguez', role: 'provider', sessionId: 'demo-provider' },
  patient: { id: 'demo-patient', email: 'patient@strixgov.com', fullName: 'Alex Johnson', role: 'patient', sessionId: 'demo-patient' },
  super_admin: { id: 'demo-super', email: 'super@strixgov.com', fullName: 'Super Admin', role: 'super_admin', sessionId: 'demo-super' },
  non_member: { id: 'demo-nonmember', email: 'guest@strixgov.com', fullName: 'Jordan Taylor', role: 'non_member', sessionId: 'demo-nonmember' },
};

function DemoAuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    try {
      const stored = localStorage.getItem(DEMO_STORAGE_KEY);
      return stored ? (JSON.parse(stored) as AuthUser) : null;
    } catch {
      return null;
    }
  });

  // Self-heal: if a demo user is present but the token is missing (e.g. a stale
  // session from before the token endpoint worked), mint one so authed tRPC
  // calls succeed without forcing a manual sign-out/sign-in.
  useEffect(() => {
    if (user && !localStorage.getItem(DEMO_TOKEN_KEY)) {
      mintDemoToken(user.role);
    }
  }, [user]);

  const signIn = async (role: AuthRole = 'admin') => {
    const demoUser = DEMO_USERS[role] ?? DEMO_USERS.patient;
    const dest = homePathForRole(role);

    // Always mint a fresh token on explicit sign-in (overwrites any stale one).
    const ok = await mintDemoToken(demoUser.role);
    if (!ok) {
      throw new Error('Sign-in could not be completed. Check that the local service is running and try again.');
    }
    localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(demoUser));
    setUser(demoUser);
    window.location.href = dest;
  };

  const signOut = () => {
    localStorage.removeItem(DEMO_STORAGE_KEY);
    localStorage.removeItem(DEMO_TOKEN_KEY);
    setUser(null);
    window.location.href = '/sign-in';
  };

  return (
    <AuthContext.Provider value={{ isLoaded: true, isSignedIn: !!user, user, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Clerk mode — only rendered inside ClerkProvider ──────────────────────

function ClerkAuthProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded, isSignedIn, user: clerkUser } = useClerkUser();
  const { signOut: clerkSignOut } = useClerkAuth();

  const user: AuthUser | null =
    isLoaded && isSignedIn && clerkUser
      ? {
          id: clerkUser.id,
          email: clerkUser.primaryEmailAddress?.emailAddress ?? '',
          fullName: clerkUser.fullName ?? '',
          role: ((clerkUser.publicMetadata as { role?: AuthRole })?.role) ?? 'patient',
          sessionId: clerkUser.id,
        }
      : null;

  const signIn = () => { window.location.href = '/sign-in'; };
  const signOut = () => { clerkSignOut().then(() => { window.location.href = '/sign-in'; }); };

  return (
    <AuthContext.Provider value={{ isLoaded, isSignedIn: isSignedIn ?? false, user, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

// ─── Unified provider — main.tsx wraps with ClerkProvider when USE_CLERK ──

export function AuthProvider({ children }: { children: React.ReactNode }) {
  return USE_CLERK
    ? <ClerkAuthProvider>{children}</ClerkAuthProvider>
    : <DemoAuthProvider>{children}</DemoAuthProvider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}

export function useUser() {
  const { user } = useAuth();
  return {
    user: user
      ? {
          id: user.id,
          fullName: user.fullName,
          emailAddresses: [{ emailAddress: user.email }],
          publicMetadata: { role: user.role },
        }
      : null,
  };
}
